import { useCallback, useEffect, useState } from 'react'
import { prop } from 'ramda'

import PlainItemsSelectorBox from 'app/common/ui-next/_filter-compilations/plain-items-selector-box'
import { subscibeTo } from 'app/common/data-box-store'
import { sortClasses } from 'app/common/lib/sort'
import { CustomerType } from 'app/dataTypes/reports/customersType'

import {
  classesListFilter,
  classesListFilterSelection,
  customerTypeFilterSelection,
  defaultClassesListDataReceiver,
  defaultClassesListSelection,
  orgsFilterSelection,
  schoolYearFilterSelection,
} from 'app/dataProviders/filter-items'

type Props = {
  id: string
  multiple?: boolean
  pending: boolean
}

const sortByTitle = sortClasses<{ title: string }>('down', prop('title'))

export const ClassesListFilter = ({ id, multiple, pending }: Props) => {
  const [isReadySchoolYear, setReadySchoolYear] = useState<boolean>(false)
  const [isReadyOrgs, setReadyOrgs] = useState<boolean>(false)
  const [isReadyCustomerType, setReadyCustomerType] = useState<boolean>(false)

  const handleClassesFilter = useCallback(() => {
    if (isReadyCustomerType && isReadyOrgs && isReadySchoolYear) {
      classesListFilter.resetToDefault()
      classesListFilterSelection.resetToDefault()
      classesListFilter.addDataReceiver(defaultClassesListDataReceiver)
      classesListFilter.dataReceive()
      classesListFilter.setStatusReady()
    }
  }, [isReadyCustomerType, isReadyOrgs, isReadySchoolYear])

  const handleOrgsFilter = useCallback(
    (data: string[]) => {
      if (data.length === 1 && data[0] !== 'empty') {
        setReadyOrgs(true)
        handleClassesFilter()
        return
      }
      classesListFilter.resetToDefault()
      classesListFilterSelection.resetToDefault()
      setReadyOrgs(false)
      setReadySchoolYear(false)
    },
    [handleClassesFilter],
  )

  const handleSchoolYearFilter = useCallback(
    (data: string[]) => {
      if (data.length === 1) {
        setReadySchoolYear(true)
        handleClassesFilter()
        return
      }
      classesListFilter.resetToDefault()
      classesListFilterSelection.resetToDefault()
      setReadySchoolYear(false)
    },
    [handleClassesFilter],
  )

  const handleCustomerTypeFilter = useCallback(
    (data: string[]) => {
      if (data.length === 1 && data[0] === CustomerType.Students) {
        setReadyCustomerType(true)
        handleClassesFilter()
        return
      }
      classesListFilter.resetToDefault()
      classesListFilterSelection.resetToDefault()
      setReadyCustomerType(false)
    },
    [handleClassesFilter],
  )

  useEffect(() => {
    const orgsUnsubsribe = subscibeTo<string[]>(orgsFilterSelection.id, handleOrgsFilter)
    const schoolYearUnsubsribe = subscibeTo<string[]>(
      schoolYearFilterSelection.id,
      handleSchoolYearFilter,
    )
    const customerTypeFilterUnsubsribe = subscibeTo<string[]>(
      customerTypeFilterSelection.id,
      handleCustomerTypeFilter,
    )

    return () => {
      orgsUnsubsribe()
      schoolYearUnsubsribe()
      customerTypeFilterUnsubsribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleClassesFilter()
  }, [handleClassesFilter, isReadyCustomerType, isReadyOrgs, isReadySchoolYear])

  return (
    isReadyCustomerType &&
    isReadyOrgs &&
    isReadySchoolYear && (
      <PlainItemsSelectorBox
        id={id}
        label="Класс"
        header="Выбор класса"
        dataBoxData={classesListFilter}
        dataBoxSelection={classesListFilterSelection}
        pleaseSelectText="Нет данных"
        selectedAllOnEmptySelection
        disabled={pending}
        sortFunc={sortByTitle}
        defaultSelection={defaultClassesListSelection}
        multiple={multiple}
      />
    )
  )
}
